import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  Menu,
  MenuItem,
  Button,
  Typography,
  Avatar,
  useTheme,
  useMediaQuery,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMore from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import { menuItems } from './SideNav';
import { useTeacher } from '../../context/TeacherContext';
import { hasReading, hasMath } from '../../utils/subjectHelpers';
import HelpButton from '../Common/HelpButton';

const NAV_GRADIENT = 'linear-gradient(90deg, #2563EB 0%, #8B5CF6 55%, #F97316 100%)';

const PARENT_NAV_PATHS = ['/dashboard', '/dashboard/assessments', '/settings'];

const getInitials = (teacher: any): string => {
  if (teacher?.firstName && teacher?.lastName) {
    return `${teacher.firstName[0]}${teacher.lastName[0]}`.toUpperCase();
  }
  return teacher?.email?.[0]?.toUpperCase() || '';
};

/**
 * Primary application navigation, rendered as a horizontal bar. Submenu parents
 * open an anchored dropdown; on small screens the whole tree moves into a drawer.
 */
const TopNavigation: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { teacher, setTeacher } = useTeacher();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [navAnchor, setNavAnchor] = useState<{ el: HTMLElement; text: string } | null>(null);
  const [accountAnchor, setAccountAnchor] = useState<null | HTMLElement>(null);

  const teacherHasReading = hasReading(teacher);
  const teacherHasMath = hasMath(teacher);
  const showAllItems = !teacherHasReading && !teacherHasMath;
  const isDevelopment = process.env.NODE_ENV === 'development';
  const isParent = teacher?.role === 'parent';

  const filteredMenuItems = useMemo(() => {
    // Homeschool parents share the Teacher record shape (models/Teacher.js,
    // role: 'parent') but none of the teacher tooling applies to them.
    if (isParent) {
      return menuItems.filter(item => PARENT_NAV_PATHS.includes(item.path || ''));
    }
    return menuItems
      .map(item => {
        if (item.items) {
          const filteredSubItems = item.items.filter(subItem => {
            if (subItem.devOnly && !isDevelopment) return false;
            if (!subItem.requiresSubject) return true;
            if (showAllItems) return true;
            if (subItem.requiresSubject === 'Reading' || subItem.requiresSubject === 'ELA') {
              return teacherHasReading;
            }
            if (subItem.requiresSubject === 'Math') {
              return teacherHasMath;
            }
            return true;
          });
          return { ...item, items: filteredSubItems };
        }
        if (item.requiresSubject) {
          if (showAllItems) return item;
          if (item.requiresSubject === 'Reading' || item.requiresSubject === 'ELA') {
            return teacherHasReading ? item : null;
          }
          if (item.requiresSubject === 'Math') {
            return teacherHasMath ? item : null;
          }
        }
        return item;
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  }, [teacherHasReading, teacherHasMath, showAllItems, isDevelopment, isParent]);

  const isSelected = (path?: string) => Boolean(path) && location.pathname === path;
  const isParentSelected = (item: { items?: { path: string }[] }) =>
    Boolean(item.items?.some(sub => isSelected(sub.path)));

  const go = (path?: string) => {
    if (!path) return;
    navigate(path);
    setNavAnchor(null);
    setMobileOpen(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setTeacher(null);
    navigate('/login');
  };

  const currentPage = location.pathname.includes('/small-group/delivery') ? 'lesson-delivery' : '';
  const openMenuItems = navAnchor
    ? filteredMenuItems.find(item => item.text === navAnchor.text)?.items ?? []
    : [];

  const brand = (
    <Box
      onClick={() => navigate('/edu-chat')}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        cursor: 'pointer',
        flexShrink: 0,
        '&:hover': { opacity: 0.85 },
      }}
    >
      <Box
        component="span"
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          backgroundColor: 'common.white',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL}/icon.png`}
          alt="Level Up Teaching"
          sx={{ width: '88%', height: '88%' }}
        />
      </Box>
      <Typography
        variant="h6"
        component="div"
        sx={{ fontSize: '1.05rem', fontWeight: 700, color: 'inherit', whiteSpace: 'nowrap' }}
      >
        Level Up Teaching
      </Typography>
    </Box>
  );

  const accountMenu = (
    <>
      {currentPage && <HelpButton page={currentPage} />}
      <IconButton size="small" onClick={(e) => setAccountAnchor(e.currentTarget)} sx={{ p: 0.5 }}>
        <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32, fontSize: '0.8rem' }}>
          {teacher && getInitials(teacher)}
        </Avatar>
      </IconButton>
      <Menu
        anchorEl={accountAnchor}
        open={Boolean(accountAnchor)}
        onClose={() => setAccountAnchor(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem dense onClick={() => { navigate('/profile'); setAccountAnchor(null); }}>
          <ListItemIcon sx={{ minWidth: 35 }}><PersonIcon fontSize="small" /></ListItemIcon>
          <Typography variant="body2">Profile</Typography>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem dense onClick={handleLogout}>
          <ListItemIcon sx={{ minWidth: 35 }}><LogoutIcon fontSize="small" color="error" /></ListItemIcon>
          <Typography variant="body2" color="error">Sign Out</Typography>
        </MenuItem>
      </Menu>
    </>
  );

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (t) => t.zIndex.drawer + 1,
        backgroundImage: NAV_GRADIENT,
        boxShadow: '0 2px 8px rgba(17, 24, 39, 0.15)',
      }}
    >
      <Toolbar variant="dense" sx={{ minHeight: 50, height: 50, gap: 1.5, px: 2 }}>
        {isMobile && (
          <IconButton edge="start" color="inherit" onClick={() => setMobileOpen(true)}>
            <MenuIcon />
          </IconButton>
        )}

        {brand}

        {!isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexGrow: 1, ml: 2 }}>
            {filteredMenuItems.map(item => {
              const active = item.items ? isParentSelected(item) : isSelected(item.path);
              return (
                <Button
                  key={item.text}
                  color="inherit"
                  startIcon={item.icon}
                  endIcon={item.items ? <ExpandMore /> : undefined}
                  onClick={(e) =>
                    item.items ? setNavAnchor({ el: e.currentTarget, text: item.text }) : go(item.path)
                  }
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.85rem',
                    fontWeight: active ? 700 : 500,
                    whiteSpace: 'nowrap',
                    borderRadius: 2,
                    px: 1.25,
                    backgroundColor: active ? 'rgba(255,255,255,0.22)' : 'transparent',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.14)' },
                  }}
                >
                  {item.text}
                </Button>
              );
            })}
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 'auto' }}>
          {accountMenu}
        </Box>
      </Toolbar>

      <Menu
        anchorEl={navAnchor?.el ?? null}
        open={Boolean(navAnchor)}
        onClose={() => setNavAnchor(null)}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        {openMenuItems.map(subItem => (
          <MenuItem
            key={subItem.text}
            dense
            selected={isSelected(subItem.path)}
            onClick={() => go(subItem.path)}
          >
            <ListItemIcon sx={{ minWidth: 35 }}>{subItem.icon}</ListItemIcon>
            <Typography variant="body2">{subItem.text}</Typography>
          </MenuItem>
        ))}
      </Menu>

      <Drawer anchor="left" open={mobileOpen} onClose={() => setMobileOpen(false)}>
        <Box sx={{ width: 260 }} role="presentation">
          <List dense>
            {filteredMenuItems.map(item =>
              item.items ? (
                <React.Fragment key={item.text}>
                  <ListSubheader sx={{ lineHeight: '32px', color: 'primary.main', fontWeight: 700 }}>
                    {item.text}
                  </ListSubheader>
                  {item.items.map(subItem => (
                    <ListItemButton
                      key={subItem.text}
                      selected={isSelected(subItem.path)}
                      onClick={() => go(subItem.path)}
                      sx={{ pl: 3 }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>{subItem.icon}</ListItemIcon>
                      <ListItemText primary={subItem.text} />
                    </ListItemButton>
                  ))}
                </React.Fragment>
              ) : (
                <ListItemButton
                  key={item.text}
                  selected={isSelected(item.path)}
                  onClick={() => go(item.path)}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              )
            )}
          </List>
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default TopNavigation;
